import { useEffect } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useAddStateMutation,
  useFetchCountriesQuery,
  useGetStateByIdQuery,
  useUpdateStateMutation,
} from '../../../apis/toolsApi';
import CrudLayout from '../../../layout/CRUDLayout';

import Dropdown from '../../../ui-components/Dropdown';
import StatusSelect from '../../../ui-components/StatusSelect';

import type { Inputs } from '../model/stateInputs';

interface StateFormProps {
  readonly embedded?: boolean;
  readonly stateId?: string;
  readonly onClose?: () => void;
  readonly onSuccess?: () => void;
}

export default function StateForm({
  embedded = false,
  stateId,
  onClose,
  onSuccess,
}: Readonly<StateFormProps>) {
  const { id } = useParams<{ id?: string }>();
  const resolvedId = stateId ?? id;
  const isEditMode = Boolean(resolvedId);

  const [addState, { isLoading }] = useAddStateMutation();
  const [updateState, { isLoading: isUpdatingState }] =
    useUpdateStateMutation();
  const { data: fetchedState } = useGetStateByIdQuery(resolvedId as string, {
    skip: !resolvedId,
  });
  const { data: countries } = useFetchCountriesQuery();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<Inputs>({
    defaultValues: {
      state: '',
      stateCode: '',
      countryId: undefined,
      status: 'Active',
    },
  });

  useEffect(() => {
    if (fetchedState?.data) {
      setValue('state', fetchedState.data.state);
      setValue('stateCode', fetchedState.data.stateCode);
      setValue('countryId', fetchedState.data.country.countryId);
      setValue('status', fetchedState.data.status);
    }
  }, [fetchedState, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      isEditMode && resolvedId
        ? await updateState({ formData: data, stateId: resolvedId }).unwrap()
        : await addState(data).unwrap();

      if (embedded) {
        onSuccess?.();
        onClose?.();
      } else {
        navigate('/states');
      }
    } catch (error: unknown) {
      console.error(error);
    }
  };

  const formFields = (
    <>
      <div className="mb-4">
        <Dropdown
          label="Country Name"
          value={control._formValues.countryId}
          setValue={(val) =>
            setValue('countryId', Number(val), { shouldValidate: true })
          }
          options={countries?.data || []}
          placeholder="Select Country Name"
          isCreatable={false}
          isMulti={false}
          required={true}
          isSearchable={true}
          labelKey="country"
          valueKey="countryId"
          isFocusAllowed
          formMode={false}
        />
        {errors.countryId && (
          <p className="error-msg">{errors.countryId.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="state" className="label">
          State Name <span className="required">*</span>
        </label>
        <input
          id="state"
          type="text"
          {...register('state', {
            required: 'Please enter a value for the State',
          })}
          className="input"
          placeholder="State Name"
        />
        {errors.state && <p className="error-msg">{errors.state.message}</p>}
      </div>

      <div className="mb-4">
        <label htmlFor="stateCode" className="label">
          State Code <span className="required">*</span>
        </label>
        <input
          id="stateCode"
          type="text"
          {...register('stateCode', {
            required: 'Please enter a value for the State Code.',
          })}
          className="input"
          placeholder="State Code"
        />
        {errors.stateCode && (
          <p className="error-msg">{errors.stateCode.message}</p>
        )}
      </div>

      <div className="mb-4">
        <StatusSelect
          label="Status"
          name="status"
          control={control}
          error={errors.status as any}
        />
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {formFields}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-4">
          <button
            type="button"
            onClick={() => (onClose ? onClose() : navigate('/states'))}
            disabled={isLoading || isUpdatingState}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUpdatingState}
            className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all text-neutral-700 tracking-wide ${
              isLoading || isUpdatingState
                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    );
  }

  return (
    <CrudLayout<Inputs>
      id={resolvedId}
      topTitle="State Management"
      title={isEditMode ? 'Update State' : 'Add State'}
      backTo="/states"
      onSubmit={onSubmit}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      isUpdating={isUpdatingState}
    >
      {formFields}
    </CrudLayout>
  );
}
